import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { SalesReportResponseV4Schema } from "../response/SalesReportResponseV4";

const API_VERSION = 'v4';

// Tipe spesifik untuk kolom yang bisa diurutkan
type OrderByOption = "property_name" | "booking_date" | "source" | "reservation_number" | "guest_name" | "checkin_date" | "checkout_date" | "extra" | "amount";

// Antarmuka untuk parameter filter
interface FilterPropsV4 {
    property_id: string;
    start_date: string;
    end_date: string;
    base_on: "stay_date" | "created_reservation" | "checkin_date" | "checkout_date";
    room_type_id?: string;
}

// Antarmuka untuk parameter hook
interface SalesReportV4Props {
    filter: FilterPropsV4;
    orderBy?: OrderByOption; // Menggunakan tipe yang sudah didefinisikan
    sort?: "asc" | "desc";
    type: string | null;
}

/**
 * Hook React Query untuk mengambil laporan penjualan v4.
 *
 * @param params - Filter, opsi pengurutan, dan tipe laporan.
 * @returns Hasil dari hook useQuery.
 */
const useSalesReportV4 = (params: SalesReportV4Props) => {
    return useQuery({
        queryKey: [
            'report',
            'sales-report-v4',
            params.type,
            JSON.stringify(params.filter),
            params.orderBy,
            params.sort,
        ],
        queryFn: async () => {
            try {
                // Menyusun body request
                const requestBody = {
                    filter: params.filter,
                    orderBy: params.orderBy || "booking_date", // orderBy default
                    sort: params.sort || "asc",             // sort default
                };

                const response = await privateApi.get(`/${API_VERSION}/channel-manager/property-report-sales`, {
                    params: requestBody
                });

                // Validasi respons dengan skema Zod
                const validationResult = SalesReportResponseV4Schema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid V4 Sales report data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch V4 Sales report`, error);
                throw error;
            }
        },
        // Mengaktifkan query hanya jika tipe laporan dan filter yang dibutuhkan ada
        enabled: !!params.filter && !!params.filter.property_id && !!params.filter.start_date && !!params.filter.end_date && params.type === "sales_report",
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useSalesReportV4;